import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut } from 'lucide-react';
import { Button, Card, Input } from '@nako/ui';
import { BrandTagline } from '@/components/BrandTagline';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';
import { makeT } from '@/lib/i18n';

const INDUSTRIES = [
  'Retail / Tuck shop',
  'Hospitality / Restaurant',
  'Transport / Kombi',
  'Transport / Logistics',
  'Healthcare / Doctor',
  'Healthcare / Dentist',
  'Healthcare / Pharmacist',
  'Healthcare / Clinic or Small Hospital',
  'Construction',
  'Beauty / Salon',
  'Tailoring / Clothing',
  'Agriculture / Farming',
  'Professional services',
  'Other',
];

export default function Signup() {
  const session = useAuthStore((s) => s.session);
  const setBusiness = useAuthStore((s) => s.setBusiness);
  const lang = useUiStore((s) => s.lang);
  const regionConfig = useRegionStore((s) => s.config);
  const taxAuth = regionConfig.taxAuthority;
  const t = makeT(lang);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [industry, setIndustry] = useState(INDUSTRIES[0]);
  const [vatRegistered, setVatRegistered] = useState(false);
  const [vatNumber, setVatNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!session) {
    navigate('/login');
    return null;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('businesses')
      .insert({
        owner_id: session.user.id,
        name,
        industry,
        vat_registered: vatRegistered,
        vat_number: vatRegistered ? vatNumber : null,
      })
      .select()
      .single();
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setBusiness(data);
    navigate('/app');
  };

  const signOutAndHome = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <main className="container-mobile py-10">
      <div className="mb-4 flex items-center justify-between">
        <Link to="/" className="text-muted hover:text-ink inline-flex items-center gap-1 text-sm">
          <ArrowLeft size={14} /> Back to home
        </Link>
        <button
          onClick={signOutAndHome}
          className="text-muted hover:text-ink inline-flex items-center gap-1 text-sm"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <header className="mb-6 text-center">
        <h1 className="font-display text-3xl font-semibold">{t('auth.signup.title')}</h1>
        <BrandTagline className="mt-2" />
        <p className="text-muted mt-2 text-sm">Dumela {session.user.email}</p>
      </header>

      <Card>
        <form onSubmit={submit} className="space-y-4">
          <Input
            label={t('auth.business_name')}
            placeholder="Mma Dipuo's Curtain Shop"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">{t('auth.industry')}</label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="min-h-tap rounded-btn border-line border bg-white px-3"
            >
              {INDUSTRIES.map((i) => (
                <option key={i}>{i}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={vatRegistered}
              onChange={(e) => setVatRegistered(e.target.checked)}
              className="h-4 w-4"
            />
            {t('auth.vat_registered')}
          </label>

          {vatRegistered && (
            <Input
              label={`${taxAuth} VAT number`}
              placeholder="P0001234567"
              value={vatNumber}
              onChange={(e) => setVatNumber(e.target.value)}
            />
          )}

          {error && <p className="text-red text-sm">{error}</p>}

          <Button type="submit" full loading={loading}>
            {t('auth.create_business')}
          </Button>
        </form>
      </Card>
    </main>
  );
}
